import { GemMark } from "@/components/c360/GemMark";
import { useLocation, useNavigate } from "react-router-dom";
import { bl, useBlur } from "@/lib/c360Mine";
import { MAIN_HINT, mainPath } from "@/lib/drawerReturn";
import { toast } from "sonner";
import { ArrowRight, Bell, ExternalLink, Mail, Phone, Copy, Star } from "lucide-react";
import { useStars } from "@/lib/stars";
import { trackContactClick, trackContactCopy } from "@/lib/tracker";
import { ViewContact } from "@/components/ViewContact";
import { UnaffiliatedBadge } from "@/components/UnaffiliatedBadge";
import { BOT_META, NOT_ENOUGH_META } from "@/components/BotCheck";
import { TIER_COLORS, CHAT, short, lastLiveColor, lastLiveText } from "@/components/LiveIntelTable";
import { GROUP_PILL } from "@/lib/liveProfile";

const BTN = "inline-flex items-center gap-2 px-3.5 py-2 rounded-md border hairline text-sm hover:border-white/30 transition-colors duration-150";

// v75 — Live Streaming Intel's own star (separate from Creator Intel and unaffiliated stars).
function LiveStar({ id }) {
  const stars = useStars("live");
  return <StarBtn on={stars.has(id)} onClick={() => stars.toggle(id)} />;
}

function StarBtn({ on, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid="lp-star" data-active={on ? "true" : "false"} aria-label={on ? "Unstar creator" : "Star creator"}
      className="p-1.5 rounded-md border hairline hover:border-amber-400/60 transition-colors duration-150">
      <Star size={18} strokeWidth={1.8} fill={on ? "#FBBF24" : "none"} style={{ color: on ? "#FBBF24" : "#71717A" }} />
    </button>
  );
}

const chip = (c) => ({ color: c, borderColor: `${c}66`, background: `${c}14` });

function Pill({ group }) {
  const frequent = group === "frequent";
  const c = frequent ? "#00F0FF" : "#A1A1AA";
  return (
    <span data-testid="lp-group-pill" data-group={group} className="mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border inline-flex items-center gap-2" style={chip(c)}>
      <span className="relative inline-flex w-1.5 h-1.5">
        {frequent && <span className="absolute inset-0 rounded-full animate-ping opacity-60" style={{ background: c }} />}
        <span className="relative w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      </span>
      {GROUP_PILL[group] || "Live streamer"}
    </span>
  );
}

export function OverallChip({ overall }) {
  const m = BOT_META[overall] || NOT_ENOUGH_META;
  return <span data-testid="lp-overall-chip" data-verdict={overall || "not_enough"} className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 rounded-md border leading-none" style={chip(m.color)}>{m.chip}</span>;
}

function StatRow({ label, children, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-6 px-4 py-3 rounded-md border hairline" style={{ background: "var(--surface-2)" }}>
      <span className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <span className="mono text-base text-right">{children}</span>
    </div>
  );
}

function Contact({ c, id }) {
  return (
    <ViewContact endpoint={c.segment === "unaffiliated" ? `/unaffiliated/creator/${id}/contact` : `/kols/${id}/contact`} channelId={id} testid="lp-view-contact"
      render={(ct) => (<>
        {ct.contact_email && (
          <a data-testid="lp-email-link" data-contact-kind="email" onClick={() => trackContactClick(id, "email")} href={`mailto:${ct.contact_email}`} className={BTN}>
            <Mail size={14} className="text-fuchsia-400" /><span className="mono text-xs text-zinc-300 truncate max-w-[220px]">{ct.contact_email}</span>
          </a>
        )}
        {ct.contact_phone && (
          <button type="button" data-testid="lp-phone-copy" data-contact-kind="phone" className={BTN}
            onClick={async () => { try { await navigator.clipboard.writeText(ct.contact_phone); trackContactCopy(id, "phone"); toast.success("Phone copied"); } catch { toast.error("Copy failed"); } }}>
            <Phone size={14} className="text-amber-400" /><span className="mono text-xs text-zinc-300">{ct.contact_phone}</span><Copy size={11} className="text-zinc-500" />
          </button>
        )}
      </>)} />
  );
}

// v83 — "Main profile →": on that very page (drawer over it) it just closes; from a drawer elsewhere Back reopens it.
export function useMainProfile(id, unaff, onLeave, onClose) {
  const nav = useNavigate();
  const loc = useLocation();
  const path = mainPath(id, unaff);
  return () => {
    if (loc.pathname === path && onClose) return onClose();
    if (onLeave) onLeave();
    return nav(path);
  };
}

export function MainProfileButton({ onClick, testid, className = BTN }) {
  return <button type="button" onClick={onClick} className={className} data-testid={testid} title={MAIN_HINT}>Main profile <ArrowRight size={14} /></button>;
}

export function LiveHeader({ c, prof, feats, perWeek, overall, showOverall, onLeave, onClose }) {
  const nav = useNavigate();
  const blur = useBlur();
  const unaff = c.segment === "unaffiliated";
  const tc = TIER_COLORS[c.tier] || "#94A3B8";
  const ch = CHAT[c.chat];
  const goMain = useMainProfile(c.id, unaff, onLeave, onClose);
  const alert = () => {
    if (onLeave) onLeave();
    if (!unaff) return nav(`/alerts?scope=${encodeURIComponent(c.id)}&scope_name=${encodeURIComponent(c.name)}`);
    const p = new URLSearchParams({ tab: "unaffiliated", sub: "alerts", scope: c.id, scope_name: c.name });
    return nav(`/search?${p.toString()}`);
  };
  const link = (href, label, kind) => href && (
    <a href={href} target="_blank" rel="noreferrer" data-testid={`lp-${kind}-link`} data-contact-kind={kind} onClick={() => trackContactClick(c.id, kind)}
      className={`mono text-[11px] text-zinc-300 hover:text-cyan-400 inline-flex items-center gap-1 ${bl(blur, "link")}`}>{label} <ExternalLink size={10} /></a>
  );
  return (
    <section className="border hairline rounded-md p-6 relative overflow-hidden" style={{ background: "var(--surface)" }} data-testid="lp-header">
      <div className="absolute -top-24 right-56 w-72 h-72 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: "radial-gradient(circle, #00F0FF 0%, transparent 60%)" }} />
      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div className="flex-1 min-w-[260px]">
          <div className="flex flex-wrap items-center gap-3">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500" data-testid="lp-overline">Live streamer profile</span>
            <Pill group={c.group} />
          </div>
          <h1 className="display text-3xl sm:text-4xl font-semibold mt-3 leading-tight flex flex-wrap items-center gap-3" data-testid="lp-name">
            <LiveStar id={c.id} />
            <span className={bl(blur)} data-testid="lp-name-text">{c.name}</span>
            <GemMark id={c.id} size={20} testid="lp-gem" />
            <span data-testid="lp-tier" className="mono text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 rounded-md border leading-none" style={chip(tc)}>Tier {c.tier}</span>
            {showOverall && <OverallChip overall={overall} />}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3" data-testid="lp-markets">
            <span className="mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Markets</span>
            {unaff ? <UnaffiliatedBadge compact /> : (prof?.markets || []).map((m) => (
              <span key={m.key} data-testid={`lp-market-${m.key}`} data-status={m.active ? "active" : "stopped"} className="mono text-[11px] inline-flex items-center gap-1.5" style={{ color: m.active ? "#39FF14" : "#F43F5E" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.active ? "#39FF14" : "#F43F5E" }} />{m.label}
              </span>
            ))}
            {(c.yt || c.tg) && <span className="text-zinc-700">|</span>}
            {link(c.yt, "YouTube", "youtube")}
            {link(c.tg, "Telegram", "telegram")}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className={`inline-flex flex-wrap items-center gap-3 ${bl(blur, "link")}`}><Contact c={c} id={c.id} /></span>
            <MainProfileButton onClick={goMain} testid="lp-full-profile" />
            <button type="button" onClick={alert} className={BTN} data-testid="lp-create-alert"><Bell size={14} className="text-cyan-400" /> Create alert</button>
          </div>
        </div>
        <div className="w-full sm:w-[360px] flex flex-col gap-2" data-testid="lp-stats">
          <StatRow label="Subscribers" testid="lp-stat-subs"><span className="text-white">{short(c.subs)}</span></StatRow>
          <StatRow label="Last live" testid="lp-stat-last">
            {c.days == null ? <span className="text-zinc-600">—</span> : <span style={{ color: lastLiveColor(c.days) }}>{lastLiveText(c.days)}</span>}
          </StatRow>
          <StatRow label="Lives per week" testid="lp-stat-perweek"><span style={{ color: "#00F0FF" }}>{perWeek == null ? "—" : perWeek}</span></StatRow>
          {feats.chat && (
            <StatRow label="Live chat" testid="lp-stat-chat">
              {ch ? <span style={{ color: ch.dot }}>{ch.label}{feats.speed && c.speed != null && <span data-testid="lp-stat-speed"> · {c.speed}/min</span>}</span> : <span className="text-zinc-600">—</span>}
            </StatRow>
          )}
        </div>
      </div>
    </section>
  );
}
